import axios from '@/helpers/interceptors'
import type { AxiosResponse } from 'axios'
import { generateUniqueHash } from '@/helpers/utils'
import { CONFIG } from '@/const/config'
import { TokenService } from '@modules/auth/services/TokenService'
import type {
  CompanyDetailsResponse,
  CompanyInfoResponse,
  CompanyStructureResponse,
  CountryDto,
  InvoiceFetchResult,
  PersonalInfoResponse,
  RegistrationTermsResponse,
  RoleDto,
  SelectCompanyRequest,
  SelectCompanyResponse,
} from './types'

/**
 * HTTP surface for the multi-step registration flow.
 *
 * Includes a couple of registration-specific quirks worth keeping in mind:
 *   - Several GETs append `generateUniqueHash()` as a cache-buster; this is a
 *     legacy pattern that should eventually be replaced with proper cache
 *     headers, but matches existing behavior.
 *   - `submitPersonalInfo` overrides the Authorization header with the
 *     stage-1 registration token (passed by the caller). The shared request
 *     interceptor honours the `isUniqueToken` flag to leave it alone.
 *   - `getInvoice` bypasses axios in favour of `fetch` because the response
 *     is binary (PDF / xlsx) and we need access to raw `Content-Disposition`.
 */
export const registrationApi = {
  submitPersonalInfo(
    payload: Record<string, unknown>,
    registrationToken: string | null,
  ): Promise<AxiosResponse<PersonalInfoResponse>> {
    return axios.post<PersonalInfoResponse>(
      '/api/registration/step1/personal-info-shareholders',
      payload,
      {
        headers: {
          Authorization: `Bearer ${registrationToken ?? ''}`,
          isUniqueToken: true,
        },
      },
    )
  },

  getCountries(): Promise<AxiosResponse<CountryDto[]>> {
    return axios.get<CountryDto[]>(
      `/api/registration/init/countries?${generateUniqueHash()}`,
      { timeout: 15000 },
    )
  },

  getCountryDocs(code: string): Promise<AxiosResponse<unknown>> {
    return axios.get(
      `/api/registration/init/country-documents/${code}?${generateUniqueHash()}`,
      { timeout: 10000 },
    )
  },

  getRoles(): Promise<AxiosResponse<RoleDto[]>> {
    return axios.get<RoleDto[]>(`/api/registration/init/roles?${generateUniqueHash()}`)
  },

  getTerms(): Promise<AxiosResponse<RegistrationTermsResponse>> {
    return axios.get<RegistrationTermsResponse>(
      `/api/registration/terms?${generateUniqueHash()}`,
    )
  },

  createCompanyInfo(payload: Record<string, unknown>): Promise<AxiosResponse<CompanyInfoResponse>> {
    return axios.post<CompanyInfoResponse>('/api/registration/step2/company-info', payload)
  },

  createCompanyStructure(payload: Record<string, unknown>): Promise<AxiosResponse<CompanyStructureResponse>> {
    return axios.post<CompanyStructureResponse>('/api/registration/step3/company-structure', payload)
  },

  loginCurrentCompany(req: SelectCompanyRequest): Promise<AxiosResponse<SelectCompanyResponse>> {
    return axios.post<SelectCompanyResponse>('/api/auth/current-company', req)
  },

  getCompanyById(id: string): Promise<AxiosResponse<CompanyDetailsResponse>> {
    return axios.get<CompanyDetailsResponse>(`/api/companies/${id}`)
  },

  paidInvoice(entityId: string): Promise<AxiosResponse<unknown>> {
    return axios.post(`/api/registration/invoice-payed/${entityId}`)
  },

  sendInvoiceEmail(email: string): Promise<AxiosResponse<unknown>> {
    return axios.post('/api/payments/invoice/email', { email })
  },

  /**
   * Fetch the onboarding invoice PDF as a binary blob. Uses `fetch` because
   * we need direct access to the `content-disposition` header for the
   * filename and a streamable arrayBuffer.
   */
  async getInvoice(): Promise<InvoiceFetchResult> {
    const baseURL = CONFIG.VUE_APP_API_URL || ''
    const token = TokenService.getAccessToken() || TokenService.getRegistrationToken()
    const response = await fetch(`${baseURL}/api/payments/invoice`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'x-api-key': CONFIG.VUE_APP_X_API_KEY,
      },
    })
    if (!response.ok) throw new Error(`Invoice request failed: ${response.status}`)
    const data = await response.arrayBuffer()
    return {
      data,
      status: response.status,
      headers: {
        'content-type': response.headers.get('content-type') || '',
        'content-disposition': response.headers.get('content-disposition') || '',
      },
    }
  },
}
